// SCR-01 로그인 (plan 8.4.1 · 11.4, mockup js/screens/login.js): centred card without the shell —
// user ID + password (show/hide, Caps Lock warning) → [로그인] (busy "로그인 중…") → "또는" → [Google로 계속하기] (only when
// the server enables it) → "계정이 없으면 …" + [초대 코드로 계정 만들기]. Steps after the password, as the server answers:
//   mfa_required → 6-digit code · locked → 15-minute lock dial with the time left (announced once a minute) ·
//   password_change_required / GET /auth/me `gates` → the first-sign-in gates (gates.tsx: password → pledge → MFA
//   enrolment → notice consent, no close button) · setupRequired (no users yet) → first admin with the one-time setup
//   code · ?invite=<code> → invite activation (invite.tsx). Wrong password: the card shakes once and says how many tries
//   are left. The shell opens only when no gate is left (RequireAuth sends a gated session back here with ?gate=1).
// Mockup-only parts (demo roles, flows, "mockup" tag, demo unlock, sample setup key) are not carried over.
import { useQueryClient } from '@tanstack/react-query';
import type { Gate } from '@aiax-hub/contracts';
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { authFlags, authKeys, fetchMe, googleStartUrl, login, pendingGates, setupFirstAdmin, submitMfa, useAuthOptions } from '../../api/auth';
import { ApiError } from '../../api/client';
import type { AuthMe, LoginResult } from '../../api/types';
import { useT } from '../../i18n';
import { reduceMotion } from '../../lib/media';
import { cls } from '../../lib/util';
import { LangSeg, ThemeButton } from '../../shell/Header';
import { Icon } from '../../ui/Icon';
import { Screen } from '../../ui/Layout';
import { Alert } from '../../ui/States';
import { toast } from '../../ui/layers';
import { GateFlow } from './gates';
import { InviteCard } from './invite';
import {
  AuthCard,
  Brand,
  CardTitle,
  EyeIcon,
  GoogleMark,
  LOCK_MIN,
  MAX_TRIES,
  PasswordRules,
  PwField,
  Spin,
  TextField,
  detailsOf,
  isLockError,
  lockedUntilOf,
  mmss,
  pwRulesOk,
  safeNext,
} from './parts';

type View = 'form' | 'mfa' | 'gates' | 'lock' | 'setup' | 'invite';
type GoogleError = 'domain' | 'cancel' | 'unlinked' | 'other';
interface ServerError {
  code?: string | null;
}

function remainingOf(e: ApiError): number | null {
  const d = detailsOf(e);
  const v = d.remaining ?? d.remainingAttempts;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

export function LoginScreen() {
  const t = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const options = useAuthOptions();
  const next = safeNext(params.get('next'));
  const hostRef = useRef<HTMLDivElement>(null);

  const [view, setView] = useState<View>(() => (params.get('invite') != null ? 'invite' : params.get('gate') ? 'gates' : 'form'));
  const [gateFirst, setGateFirst] = useState<Gate[]>(() => (params.get('gate') === 'password' ? ['password_change'] : []));
  const [gateRun, setGateRun] = useState(0);
  const [inviteCode, setInviteCode] = useState(() => params.get('invite') ?? '');
  const [id, setId] = useState('');
  const [pw, setPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [caps, setCaps] = useState(false);
  const [busy, setBusy] = useState(false);
  const [gBusy, setGBusy] = useState(false);
  const [idErr, setIdErr] = useState<string | null>(null);
  const [pwErr, setPwErr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [serverErr, setServerErr] = useState<ServerError | null>(null);
  const [gerr, setGerr] = useState<GoogleError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const [code, setCode] = useState('');
  const [mfaErr, setMfaErr] = useState<string | null>(null);
  const [setup, setSetup] = useState({ code: '', loginId: '', displayName: '', email: '', p1: '', p2: '', err: null as string | null, bad: '' });
  const [lockUntil, setLockUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const tempPw = useRef<string | null>(null);
  const focusReq = useRef<{ sel: string; select?: boolean } | null>(null);
  const [focusTick, setFocusTick] = useState(0);
  const focusLater = (sel: string, select?: boolean) => {
    focusReq.current = { sel, select };
    setFocusTick((n) => n + 1);
  };
  useEffect(() => {
    const req = focusReq.current;
    if (!req) return;
    focusReq.current = null;
    const el = hostRef.current?.querySelector<HTMLInputElement>(req.sel);
    if (el) {
      el.focus();
      if (req.select && typeof el.select === 'function') el.select();
    }
  }, [focusTick, view]);

  // one-time messages carried by the address or the session: Google error (?gerr=), session ended (401)
  useEffect(() => {
    const g = params.get('gerr');
    if (g) setGerr(['domain', 'cancel', 'unlinked'].includes(g) ? (g as GoogleError) : 'other');
    // Keep the invite in component memory only; replace the credential-bearing browser history entry.
    if (g || params.get('invite')) {
      const rest = new URLSearchParams(params);
      rest.delete('gerr');
      if (rest.has('invite')) rest.set('invite', '');
      const s = rest.toString();
      navigate({ search: s ? `?${s}` : '' }, { replace: true });
    }
    if (authFlags.expired) {
      authFlags.expired = false;
      setNotice(t('login.expired'));
    }
    // once on arrival
  }, []);

  // no users yet → first admin with the one-time setup code
  useEffect(() => {
    if (options.data?.setupRequired && view === 'form') setView('setup');
  }, [options.data?.setupRequired, view]);

  // lock countdown: once a second; the screen-reader text changes only when the minute changes
  useEffect(() => {
    if (view !== 'lock') return;
    const tm = setInterval(() => {
      const n = Date.now();
      setNow(n);
      if (lockUntil - n <= 0) {
        setView('form');
        setError(null);
        toast(t('login.lock.ended'), 'info');
        focusLater('#lg-pw');
      }
    }, 1000);
    return () => clearInterval(tm);
  }, [view, lockUntil, t]);

  /** Every gate passed: welcome and go where the person was going. */
  function enter(me: AuthMe) {
    tempPw.current = null;
    toast(t('login.welcome', { name: me.user.displayName }), 'ok');
    navigate(next ?? '/home', { replace: true });
  }

  function openGates(first: Gate[]) {
    setGateFirst(first);
    setGateRun((n) => n + 1);
    setView('gates');
  }

  async function finish() {
    const me = await qc.fetchQuery({ queryKey: authKeys.me, queryFn: () => fetchMe(), staleTime: 0 });
    if (!me) {
      setServerErr({});
      setView('form');
      return;
    }
    if (pendingGates(me).length) openGates([]);
    else enter(me);
  }

  function lock(until: number) {
    setLockUntil(until);
    setNow(Date.now());
    setPw('');
    setError(null);
    setView('lock');
    focusLater('#lg-title');
  }

  async function afterStatus(r: LoginResult | undefined) {
    if (!r || r.status === 'ok') return finish();
    if (r.status === 'mfa_required') {
      setCode('');
      setMfaErr(null);
      setView('mfa');
      focusLater('#lg-code');
    } else if (r.status === 'password_change_required') openGates(['password_change']);
    else lock(lockedUntilOf(null, r.lockedUntil));
  }

  function serverFailure(e: unknown) {
    setServerErr({ code: e instanceof ApiError ? (e.requestId ?? (e.status ? e.code : null)) : null });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setGerr(null);
    setError(null);
    setServerErr(null);
    setNotice(null);
    const idE = id.trim() ? null : t('login.err.id');
    const pwE = pw ? null : t('login.err.pw');
    setIdErr(idE);
    setPwErr(pwE);
    if (idE || pwE) {
      focusLater(idE ? '#lg-id' : '#lg-pw');
      return;
    }
    setBusy(true);
    try {
      const r = await login({ loginId: id.trim(), password: pw });
      tempPw.current = pw;
      await afterStatus(r);
    } catch (err) {
      if (err instanceof ApiError && isLockError(err)) lock(lockedUntilOf(err));
      else if (err instanceof ApiError && (err.status === 401 || err.status === 400)) {
        const left = remainingOf(err);
        if (left === 0) lock(lockedUntilOf(err));
        else {
          setError(left != null ? t('login.err.wrong', { n: left }) + (left === 1 ? ' ' + t('login.err.lastHint', { min: LOCK_MIN }) : '') : t('login.err.wrongPlain'));
          setShake(!reduceMotion());
          focusLater('#lg-pw', true);
        }
      } else serverFailure(err);
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (!/^\d{6}$/.test(code)) {
      setMfaErr(t('login.mfa.err'));
      focusLater('#lg-code');
      return;
    }
    setMfaErr(null);
    setBusy(true);
    try {
      await afterStatus(await submitMfa({ code }));
    } catch (err) {
      if (err instanceof ApiError && isLockError(err)) lock(lockedUntilOf(err));
      else if (err instanceof ApiError && (err.status === 400 || err.status === 401 || err.status === 403)) {
        setMfaErr(t('login.mfa.errWrong'));
        setCode('');
        focusLater('#lg-code');
      } else serverFailure(err);
    } finally {
      setBusy(false);
    }
  }

  async function submitSetup(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    const s = setup;
    const order: Array<[keyof typeof s, string]> = [
      ['code', '#lg-su-code'],
      ['loginId', '#lg-su-id'],
      ['displayName', '#lg-su-name'],
      ['email', '#lg-su-email'],
      ['p1', '#lg-gp1'],
      ['p2', '#lg-gp2'],
    ];
    const empty = order.find(([k]) => !String(s[k] ?? '').trim());
    if (empty) {
      setSetup((x) => ({ ...x, err: t('login.setup.err.required'), bad: empty[0] }));
      focusLater(empty[1]);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.email.trim())) {
      setSetup((x) => ({ ...x, err: t('login.setup.err.email'), bad: 'email' }));
      focusLater('#lg-su-email');
      return;
    }
    if (!pwRulesOk(s.p1, s.p2, s.loginId)) {
      setSetup((x) => ({ ...x, err: t('login.gate.pw.err'), bad: 'p1' }));
      focusLater('#lg-gp1');
      return;
    }
    setSetup((x) => ({ ...x, err: null, bad: '' }));
    setBusy(true);
    try {
      const r = await setupFirstAdmin({ code: s.code.trim(), loginId: s.loginId.trim(), password: s.p1, displayName: s.displayName.trim(), email: s.email.trim() });
      void qc.invalidateQueries({ queryKey: authKeys.options });
      if (r) {
        await afterStatus(r);
        return;
      }
      // created but not signed in: sign in with the new account
      setId(s.loginId.trim());
      setPw('');
      setView('form');
      toast(t('login.setup.done'), 'ok');
      focusLater('#lg-pw');
    } catch (err) {
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        const which = /email/i.test(err.code) ? 'email' : /password/i.test(err.code) ? 'p1' : 'code';
        setSetup((x) => ({ ...x, err: which === 'email' ? t('login.setup.err.email') : which === 'p1' ? t('login.gate.pw.errServer') : t('login.setup.err.code'), bad: which }));
        focusLater(which === 'email' ? '#lg-su-email' : which === 'p1' ? '#lg-gp1' : '#lg-su-code');
      } else serverFailure(err);
    } finally {
      setBusy(false);
    }
  }

  const onCaps = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (typeof e.getModifierState === 'function') setCaps(e.getModifierState('CapsLock'));
  };
  const backToForm = () => {
    setView('form');
    setCode('');
    setMfaErr(null);
    setPw('');
    tempPw.current = null;
    focusLater('#lg-pw');
  };
  const startGoogle = () => {
    if (gBusy || busy) return;
    setGerr(null);
    setError(null);
    setGBusy(true);
    window.location.assign(googleStartUrl(next));
  };

  const alerts: ReactNode[] = [];
  if (notice) alerts.push(<Alert key="notice" tone="info" title={notice} />);
  if (serverErr)
    alerts.push(
      <Alert
        key="server"
        tone="danger"
        title={t('login.err.server')}
        desc={serverErr.code ? `${t('state.errCode')} ${serverErr.code}` : undefined}
        action={{ label: t('common.retry'), onClick: () => setServerErr(null) }}
      />,
    );
  if (gerr) alerts.push(<Alert key="gerr" tone="warn" title={t(gerr === 'domain' ? 'login.gerr.domainPlain' : 'login.gerr.' + gerr)} />);
  const alertBox = alerts.length ? <div className="lg-alerts">{alerts}</div> : null;

  let card: ReactNode;
  if (view === 'lock') {
    const total = LOCK_MIN * 60_000;
    const left = Math.max(0, lockUntil - now);
    card = (
      <AuthCard variant="lg-card--lock">
        <div className="lg-dial">
          <svg viewBox="0 0 120 120" className="lg-dial__ring" aria-hidden="true" focusable="false">
            <circle cx="60" cy="60" r="52" className="lg-dial__track" pathLength={100} />
            <circle cx="60" cy="60" r="52" className="lg-dial__arc" pathLength={100} style={{ strokeDashoffset: (100 - Math.min(1, left / total) * 100).toFixed(2) }} />
          </svg>
          <span className="lg-dial__lock" aria-hidden="true">
            <Icon name="lock" />
          </span>
          <b className="lg-dial__time" aria-hidden="true">
            {mmss(left)}
          </b>
        </div>
        <CardTitle>{t('login.lock.title')}</CardTitle>
        <p className="auth-card__sub">{t('login.lock.desc', { n: MAX_TRIES, min: LOCK_MIN })}</p>
        <p className="lg-lock__left">
          <Icon name="clock" />
          <span>{t('login.lock.left', { mmss: mmss(left) })}</span>
        </p>
        <p className="visually-hidden" aria-live="polite">
          {t('login.lock.leftMin', { n: Math.max(1, Math.ceil(left / 60_000)) })}
        </p>
        <p className="auth-card__note">{t('login.forgot')}</p>
      </AuthCard>
    );
  } else if (view === 'mfa') {
    card = (
      <AuthCard variant="lg-card--mfa">
        <Brand />
        <p className="lg-kicker">
          <Icon name="shield" />
          {t('login.mfa.kicker')}
        </p>
        <CardTitle>{t('login.mfa.title')}</CardTitle>
        <p className="auth-card__sub">{t('login.mfa.desc', { name: id.trim() })}</p>
        {alertBox}
        <form className="form lg-form" noValidate aria-busy={busy || undefined} onSubmit={submitCode}>
          <div className="field">
            <label className="field__label" htmlFor="lg-code">
              {t('login.mfa.code')}
            </label>
            <input
              className="input lg-input lg-code"
              id="lg-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              pattern="[0-9]*"
              placeholder="000000"
              value={code}
              aria-invalid={mfaErr ? true : undefined}
              aria-describedby={'lg-code-hint' + (mfaErr ? ' lg-code-err' : '')}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            />
            <p className="field__hint" id="lg-code-hint">
              {t('login.mfa.hint')}
            </p>
            {mfaErr ? (
              <p className="field__error" id="lg-code-err" role="alert">
                {mfaErr}
              </p>
            ) : null}
          </div>
          <button className={cls('btn btn--primary btn--lg btn--block', busy && 'is-busy')} type="submit" aria-disabled={busy || undefined}>
            {busy ? <Spin /> : null}
            {t('login.mfa.submit')}
          </button>
        </form>
        <button className="btn btn--ghost btn--block lg-back" type="button" onClick={backToForm}>
          <Icon name="chevL" />
          {t('login.mfa.other')}
        </button>
        <p className="auth-card__note">{t('login.mfa.help')}</p>
      </AuthCard>
    );
  } else if (view === 'gates') {
    card = (
      <GateFlow
        key={gateRun}
        first={gateFirst}
        tempPw={tempPw.current}
        loginId={id.trim()}
        onDone={enter}
        onLocked={lock}
        onSignedOut={() => {
          tempPw.current = null;
          setNotice(t('login.expired'));
          setView('form');
        }}
        onLeave={() => {
          tempPw.current = null;
          setPw('');
          toast(t('login.loggedOut'), 'ok');
          setView('form');
          focusLater('#lg-id');
        }}
      />
    );
  } else if (view === 'invite') {
    card = (
      <InviteCard
        initialCode={inviteCode}
        onBack={() => {
          setView('form');
          focusLater('#lg-id');
        }}
        onCreated={(loginId) => {
          setId(loginId);
          setPw('');
          setInviteCode('');
          setView('form');
          focusLater('#lg-pw');
        }}
      />
    );
  } else if (view === 'setup') {
    const s = setup;
    const upd = (k: keyof typeof s) => (v: string) => setSetup((x) => ({ ...x, [k]: v }));
    const described = 'lg-gp-rules' + (s.err ? ' lg-su-err' : '');
    card = (
      <AuthCard variant="lg-card--gate">
        <Brand />
        <p className="lg-kicker">
          <Icon name="key" />
          {t('login.setup.kicker')}
        </p>
        <CardTitle>{t('login.setup.title')}</CardTitle>
        <p className="auth-card__sub">{t('login.setup.desc')}</p>
        {alertBox}
        <form className="form lg-form" noValidate aria-busy={busy || undefined} onSubmit={submitSetup}>
          <TextField id="lg-su-code" label={t('login.setup.code')} value={s.code} onChange={upd('code')} autoComplete="one-time-code" hint={t('login.setup.codeHint')} error={s.bad === 'code'} />
          <TextField id="lg-su-id" label={t('login.id')} value={s.loginId} onChange={upd('loginId')} autoComplete="username" inputMode="email" error={s.bad === 'loginId'} />
          <TextField id="lg-su-name" label={t('login.setup.displayName')} value={s.displayName} onChange={upd('displayName')} autoComplete="name" error={s.bad === 'displayName'} />
          <TextField id="lg-su-email" label={t('login.setup.email')} value={s.email} onChange={upd('email')} type="email" autoComplete="email" inputMode="email" error={s.bad === 'email'} />
          <PwField id="lg-gp1" label={t('login.gate.pw.new')} value={s.p1} onChange={upd('p1')} invalid={s.bad === 'p1'} describedBy={described} />
          <PwField id="lg-gp2" label={t('login.gate.pw.confirm')} value={s.p2} onChange={upd('p2')} invalid={s.bad === 'p2'} describedBy={described} />
          <PasswordRules id="lg-gp-rules" p1={s.p1} p2={s.p2} loginId={s.loginId} />
          {s.err ? (
            <p className="field__error" id="lg-su-err" role="alert">
              {s.err}
            </p>
          ) : null}
          <button className={cls('btn btn--primary btn--lg btn--block', busy && 'is-busy')} type="submit" aria-disabled={busy || undefined}>
            {busy ? <Spin /> : null}
            <span>{t(busy ? 'login.setup.busy' : 'login.setup.submit')}</span>
          </button>
        </form>
      </AuthCard>
    );
  } else {
    const pwMsg = pwErr || error;
    card = (
      <AuthCard shake={shake} onShakeEnd={() => setShake(false)}>
        <Brand />
        <CardTitle>{t('login.title')}</CardTitle>
        <p className="auth-card__sub">{t('login.sub')}</p>
        {alertBox}
        <form className="form lg-form" noValidate aria-busy={busy || undefined} onSubmit={submit}>
          <div className="field">
            <label className="field__label" htmlFor="lg-id">
              {t('login.id')}
            </label>
            <input
              className="input lg-input"
              id="lg-id"
              name="username"
              type="text"
              inputMode="email"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              value={id}
              placeholder={t('login.idPh')}
              readOnly={busy}
              aria-invalid={idErr ? true : undefined}
              aria-describedby={idErr ? 'lg-id-err' : undefined}
              onChange={(e) => {
                setId(e.target.value);
                if (idErr) setIdErr(null);
              }}
            />
            {idErr ? (
              <p className="field__error" id="lg-id-err" role="alert">
                {idErr}
              </p>
            ) : null}
          </div>
          <div className="field">
            <div className="lg-label-row">
              <label className="field__label" htmlFor="lg-pw">
                {t('login.pw')}
              </label>
              <span className={cls('lg-caps', caps && 'is-on')} id="lg-caps" aria-live="polite">
                {caps ? t('login.caps') : ''}
              </span>
            </div>
            <div className="lg-pwbox">
              <input
                className="input lg-input lg-input--pw"
                id="lg-pw"
                name="password"
                type={showPw ? 'text' : 'password'}
                autoComplete="current-password"
                value={pw}
                placeholder={t('login.pwPh')}
                readOnly={busy}
                aria-invalid={pwMsg ? true : undefined}
                aria-describedby={'lg-caps' + (pwMsg ? ' lg-pw-err' : '')}
                onChange={(e) => {
                  setPw(e.target.value);
                  if (pwErr) setPwErr(null);
                }}
                onKeyDown={onCaps}
                onKeyUp={onCaps}
              />
              <button
                className="lg-eye"
                type="button"
                aria-controls="lg-pw"
                aria-pressed={showPw}
                aria-label={t(showPw ? 'login.pwHide' : 'login.pwShow')}
                data-tip={t(showPw ? 'login.pwHide' : 'login.pwShow')}
                onClick={() => setShowPw((v) => !v)}
              >
                <EyeIcon />
              </button>
            </div>
            {pwMsg ? (
              <p className="field__error" id="lg-pw-err" role="alert">
                {pwMsg}
              </p>
            ) : null}
          </div>
          <button className={cls('btn btn--primary btn--lg btn--block lg-submit', busy && 'is-busy')} type="submit" aria-disabled={busy || undefined}>
            {busy ? <Spin /> : null}
            <span>{t(busy ? 'login.busy' : 'login.submit')}</span>
          </button>
        </form>
        {options.data?.google ? (
          <>
            <p className="divider">{t('login.or')}</p>
            <button className={cls('btn btn--lg btn--block lg-google', gBusy && 'is-busy')} type="button" aria-describedby="lg-g-hint" onClick={startGoogle}>
              {gBusy ? <Spin /> : <GoogleMark />}
              <span>{t(gBusy ? 'login.googleBusy' : 'login.google')}</span>
            </button>
            <p className="field__hint lg-ghint" id="lg-g-hint">
              {t('login.googleHintPlain')}
            </p>
          </>
        ) : null}
        <p className="auth-card__note">
          {t('login.noAccount')} {t('login.forgot')}
        </p>
        <p className="auth-card__note lg-invite-link">
          <button
            className="link-btn"
            type="button"
            onClick={() => {
              setInviteCode('');
              setView('invite');
            }}
          >
            <Icon name="key" />
            {t('login.invite.link')}
          </button>
        </p>
      </AuthCard>
    );
  }

  return (
    <Screen name="login">
      <div className="lg-top">
        <span className="lg-top__tag" />
        <div className="lg-top__acts">
          <LangSeg />
          <ThemeButton className="lg-theme" />
        </div>
      </div>
      <div className="lg-wrap">
        <div className="lg-host" ref={hostRef}>
          {card}
        </div>
      </div>
    </Screen>
  );
}
